import * as React from "react";
import { cn } from "@/lib/utils";
import { FieldProvider, useFieldWiring, type FieldProps } from "./Field";

type Props = FieldProps;

/** Settings-style form row: fixed 200px label column beside the control column,
 *  separated from the row above by a hairline. Collapses to a stacked label +
 *  full-width control below 640px. Same wiring as Field — only the layout
 *  differs — so controls inside pick up id / describedby / error identically,
 *  including `group` (see the Field docblock for when to set it). */
export function FormRow({
  label,
  hint,
  help,
  error,
  id,
  group,
  children,
  className,
}: Props) {
  const f = useFieldWiring({ id, help, error, group });

  const labelContent = (
    <>
      {label}
      {hint && <span className="vb-form-row__hint">{hint}</span>}
    </>
  );

  return (
    <div
      className={cn("vb-form-row", className)}
      role={group ? "group" : undefined}
      aria-labelledby={group ? f.labelId : undefined}
    >
      {group ? (
        <span id={f.labelId} className="vb-form-row__label">
          {labelContent}
        </span>
      ) : (
        <label className="vb-form-row__label" htmlFor={f.id}>
          {labelContent}
        </label>
      )}

      <div className="vb-form-row__control">
        <FieldProvider value={f.wiring}>{children}</FieldProvider>

        {f.hasMessage && (
          <span
            id={f.messageId}
            className={cn("vb-field__help", f.isError && "vb-field__help--error")}
          >
            {f.message}
          </span>
        )}
      </div>
    </div>
  );
}

export default FormRow;
